import React, { useState, useEffect } from 'react';
import { Database, Calendar, Activity, RefreshCw, TrendingUp } from 'lucide-react';
import { api as axios } from '../lib/api';
import { useI18n } from '../i18n/i18n';

interface SummaryData {
  totalRecords: number;
  lastTimestamp: string | null;
  averagePUE: number | null;
  timeRange: {
    hours: number;
    from: string | null;
    to: string | null;
  };
  metadata: {
    schema: string;
    table: string;
    sourceTable: string;
    queriedAt: string;
  };
}

const SummaryWidget: React.FC = () => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('24h');
  const { t } = useI18n();

  const timePeriods = [
    { value: '1h', label: '1H' },
    { value: '2h', label: '2H' },
    { value: '4h', label: '4H' },
    { value: '12h', label: '12H' },
    { value: '24h', label: '24H' },
    { value: '2d', label: '2D' },
    { value: '3d', label: '3D' },
    { value: '1w', label: '1W' },
    { value: '1M', label: '1M' }
  ];

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get<SummaryData>(`/api/summary?period=${selectedPeriod}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Summary fetch failed:', err);
      setError('Failed to fetch database summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchSummary, 60000);
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return 'No data';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getPUEStatus = (pue: number | null) => {
    if (!pue) return { text: 'No data', color: 'text-gray-500' };
    
    if (pue <= 1.2) return { text: 'Excellent', color: 'text-success-600' };
    if (pue <= 1.5) return { text: 'Good', color: 'text-primary-600' };
    if (pue <= 2.0) return { text: 'Average', color: 'text-warning-600' };
    return { text: 'Poor', color: 'text-error-600' };
  };

  return (
    <div className="card card-hover p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Database className="h-6 w-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('summary.title')}</h3>
        </div>
        <button
          onClick={fetchSummary}
          disabled={loading}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="text-center py-8">
          <Database className="h-12 w-12 text-error-500 mx-auto mb-3" />
            <p className="text-error-600 font-medium">{t('summary.error.title')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('summary.error.body')}</p>
          <button
            onClick={fetchSummary}
            className="btn-primary mt-3 text-sm"
          >
            {t('summary.retry')}
          </button>
        </div>
      ) : loading && !summary ? (
        <div className="text-center py-8">
          <div className="loading-spinner h-8 w-8 mx-auto mb-3"></div>
          <p className="text-gray-500">{t('summary.loading')}</p>
        </div>
      ) : summary ? (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Total Records */}
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Database className="h-5 w-5 text-primary-600" />
                <span className="text-sm font-medium text-primary-600">{t('summary.totalRecords')}</span>
              </div>
              <p className="text-2xl font-bold text-primary-700 mt-1">
                {formatNumber(summary.totalRecords)}
              </p>
            </div>

            {/* Average PUE */}
            <div className="bg-gradient-to-r from-success-50 to-success-100 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-success-600" />
                <span className="text-sm font-medium text-success-600">{t('summary.avgPue')}</span>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-2xl font-bold text-success-700">
                  {summary.averagePUE?.toFixed(3) || 'N/A'}
                </p>
                {summary.averagePUE && (
                  <span className={`text-sm font-medium ${getPUEStatus(summary.averagePUE).color}`}>
                    {getPUEStatus(summary.averagePUE).text}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Latest Data */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>{t('summary.latest')}</span>
            </h4>
            
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('summary.lastTimestamp')}</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatTimestamp(summary.lastTimestamp)}
                </span>
              </div>
              
              {summary.timeRange.from && summary.timeRange.to && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{t('summary.analysisPeriod')}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedPeriod.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Time Period Selection Buttons */}
                  <div className="space-y-2">
                    <span className="text-xs text-gray-500">{t('summary.selectPeriod')}</span>
                    <div className="flex flex-wrap gap-1">
                      {timePeriods.map((period) => (
                        <button
                          key={period.value}
                          onClick={() => setSelectedPeriod(period.value)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            selectedPeriod === period.value
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          disabled={loading}
                        >
                          {period.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Source Info */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <Activity className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">{t('summary.dataSource')}</span>
            </div>
            
            <div className="text-xs text-gray-500 space-y-1">
              <p>{t('summary.schema')} <span className="font-mono">{summary.metadata.schema}</span></p>
              <p>{t('summary.table')} <span className="font-mono">{summary.metadata.table}</span></p>
              <p>{t('summary.sourceTable')} <span className="font-mono">{summary.metadata.sourceTable}</span></p>
              <p>{t('summary.queried')} {new Date(summary.metadata.queriedAt).toLocaleString()}</p>
            </div>
          </div>

          {/* PUE Information */}
          {summary.averagePUE && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">{t('summary.pueExplainer')}</p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SummaryWidget;
